/**
 * hercat cart goals bar (2026-11-03)
 *
 * Companion to snippets/free-shipping-bar.liquid. The snippet renders the correct state server-side and the cart
 * drawer re-renders it on every cart change, so this element only has two jobs:
 *   1. animate the fill from the previously shown progress to the new one when the bar is re-rendered;
 *   2. keep the message, fill and milestones in sync on "cart:change" for any page that is not re-rendered.
 */

let lastProgress = null;

const currencyFormatter = () => new Intl.NumberFormat(Shopify.locale, {
  style: 'currency',
  currency: Shopify.currency.active,
});

const fill = (template, values) => Object.keys(values).reduce(
  (text, key) => text.split(`[${key}]`).join(values[key]),
  template,
);

class HercatCartGoals extends HTMLElement {
  static get observedAttributes() {
    return ['total-price'];
  }

  connectedCallback() {
    this._onCartChange = (event) => {
      const cart = event.detail && event.detail.cart;
      if (!cart || !Array.isArray(cart.items)) return;
      const items = cart.items
        .filter((item) => item.requires_shipping)
        .reduce((sum, item) => sum + item.final_line_price, 0);
      const discount = (cart.cart_level_discount_applications || [])
        .reduce((sum, application) => sum + application.total_allocated_amount, 0);
      this.setAttribute('total-price', Math.max(0, items - discount));
    };
    document.addEventListener('cart:change', this._onCartChange);

    const fillElement = this.querySelector('.cart-goals__fill');
    const target = this.progress;

    if (fillElement && lastProgress !== null && Math.abs(lastProgress - target) > 0.001) {
      fillElement.classList.add('is-settling');
      fillElement.style.setProperty('--progress', lastProgress);
      // Force a layout pass so the browser paints the old width before transitioning to the new one.
      void fillElement.offsetWidth;
      fillElement.classList.remove('is-settling');
      requestAnimationFrame(() => fillElement.style.setProperty('--progress', target));
    }

    lastProgress = target;
  }

  disconnectedCallback() {
    document.removeEventListener('cart:change', this._onCartChange);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== null && oldValue !== newValue) {
      this.render();
    }
  }

  get totalPrice() {
    return parseFloat(this.getAttribute('total-price')) || 0;
  }

  get goals() {
    const goals = [];
    for (const index of [1, 2]) {
      const amount = parseFloat(this.getAttribute(`goal-${index}-amount`));
      if (amount > 0) {
        goals.push({ amount, label: this.getAttribute(`goal-${index}-label`) || '' });
      }
    }
    return goals;
  }

  get progress() {
    const goals = this.goals;
    if (goals.length === 0) return 0;
    const max = goals[goals.length - 1].amount;
    return Math.min(1, Math.max(0, this.totalPrice / max));
  }

  render() {
    const goals = this.goals;
    if (goals.length === 0) return;

    const total = this.totalPrice;
    const max = goals[goals.length - 1].amount;
    const progress = this.progress;
    const money = (cents) => currencyFormatter().format(cents / 100);

    const fillElement = this.querySelector('.cart-goals__fill');
    if (fillElement) fillElement.style.setProperty('--progress', progress);
    lastProgress = progress;

    const track = this.querySelector('.cart-goals__track');
    if (track) track.setAttribute('aria-valuenow', Math.min(total, max));

    this.querySelectorAll('.cart-goals__milestone').forEach((milestone) => {
      const goal = goals[parseInt(milestone.dataset.goal, 10) - 1];
      milestone.classList.toggle('is-reached', !!goal && total >= goal.amount);
    });

    const message = this.querySelector('.cart-goals__message');
    if (!message) return;

    const [first, second] = goals;
    if (total < first.amount) {
      message.innerHTML = fill(this.getAttribute('message-unreached') || '', {
        goal: first.label,
        remaining_amount: money(first.amount - total),
      });
    } else if (second && total < second.amount) {
      message.innerHTML = fill(this.getAttribute('message-next') || '', {
        previous_goal: first.label,
        goal: second.label,
        remaining_amount: money(second.amount - total),
      });
    } else if (second) {
      message.innerHTML = fill(this.getAttribute('message-all-reached') || '', {
        goal_1: first.label,
        goal_2: second.label.toLowerCase(),
      });
    } else {
      message.innerHTML = fill(this.getAttribute('message-reached') || '', { goal: first.label });
    }
  }
}

if (!window.customElements.get('hercat-cart-goals')) {
  window.customElements.define('hercat-cart-goals', HercatCartGoals);
}
